import { DOCUMENT, inject } from '@angular/core';
import { NavigationError } from '@angular/router';

import { BootHealthService } from '../services/boot-health/boot-health.service';

const RELOAD_GUARD_KEY = 'chunkReloadAttempt';
const RELOAD_GUARD_WINDOW_MS = 30_000;

const CHUNK_LOAD_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
];

interface ReloadGuard {
  url: string;
  ts: number;
}

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return CHUNK_LOAD_ERROR_PATTERNS.some(pattern => error.message.includes(pattern));
}

// sessionStorage can throw (storage disabled, quota exceeded). The guard is a
// best-effort loop break — if storage is unavailable we still attempt recovery
// and accept that we lose loop protection rather than refusing to navigate.
function readGuard(): ReloadGuard | null {
  try {
    const raw = sessionStorage.getItem(RELOAD_GUARD_KEY);
    if (!raw) return null;
    const sepIdx = raw.lastIndexOf('|');
    if (sepIdx === -1) return null;
    const ts = Number(raw.slice(sepIdx + 1));
    if (!Number.isFinite(ts)) return null;
    return { url: raw.slice(0, sepIdx), ts };
  } catch {
    return null;
  }
}

function writeGuard(url: string): void {
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, `${url}|${Date.now()}`);
  } catch {
    // ignore — see readGuard.
  }
}

function clearGuard(): void {
  try {
    sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    // ignore — see readGuard.
  }
}

/**
 * Recovers from stale-chunk navigation failures after a deploy.
 *
 * When a new build replaces hashed chunk filenames, an already-open tab
 * still references the old names. The SPA fallback serves `index.html` for
 * the missing chunk, the browser rejects it as a module (MIME mismatch),
 * and the dynamic import in `loadComponent` rejects with a TypeError.
 *
 * Recovery is a two-step navigation:
 *
 *   1. `fetch(targetUrl, { cache: 'reload' })` — issued ASAP, runs to
 *      completion before the navigation. `cache: 'reload'` is the only
 *      fetch directive that fully bypasses the browser HTTP cache. The
 *      server returns the fresh `index.html` (CloudFront maps the SPA
 *      route to `index.html` via custom error responses), carrying
 *      `Cache-Control: no-store`. The browser then either:
 *        • re-uses no-store rules and doesn't store the entry, so the
 *          subsequent navigation is also fresh, OR
 *        • overwrites a previously-pinned long-lived cache entry with
 *          the new no-store response — clearing the stale shell.
 *      Without this step, users whose `index.html` was cached BEFORE
 *      `Cache-Control: no-store` was applied to it stayed locked: a
 *      plain `location.assign(targetUrl)` would re-use that stale entry,
 *      re-attempt the same missing chunk, and exit on the guard below
 *      on the second pass. The app-loader is gated on `NavigationEnd`,
 *      so the user sees the loader forever.
 *
 *   2. `location.assign(targetUrl)` — navigate to the route the user
 *      was trying to reach. Now reads from the freshly-primed cache,
 *      gets the current `index.html` with the current chunk hashes,
 *      and the navigation succeeds.
 *
 * A short-lived sessionStorage guard prevents an infinite reload loop
 * if the chunk is still missing after the first attempt (e.g., the
 * deploy is genuinely broken), but expires after 30 s so a later
 * deploy in the same tab can still be recovered.
 *
 * Runs in `withNavigationErrorHandler`'s injection context, so `inject`
 * is available. The async cache-prime is launched fire-and-forget so
 * the handler's return type stays `void`; navigation only happens
 * after the prime resolves, so there's no race.
 */
export function handleRouterNavigationError(event: NavigationError): void {
  // Non-chunk navigation errors (guard threw, resolver threw, an
  // authorised-redirect that itself failed) used to strand the boot
  // loader because `NavigationEnd` never fires in those cases and
  // `BootHealthService`'s `navDone` would stay false forever. Mark
  // nav-as-settled here so the loader unblocks and the user sees
  // whichever error route Angular routed them to. Chunk errors
  // skip this — they're heading for a fresh-shell reload, and we
  // want the loader to stay up during that reload, not the boot-
  // error UI.
  if (!isChunkLoadError(event.error)) {
    inject(BootHealthService).markInitialNavSettled();
    return;
  }

  const targetUrl = event.url;
  const guard = readGuard();
  if (guard && guard.url === targetUrl && Date.now() - guard.ts < RELOAD_GUARD_WINDOW_MS) {
    // Second attempt at the same URL within the window — the deploy is
    // genuinely broken or the chunk hash STILL doesn't match after a
    // forced refresh. Clear the guard so a future deploy can recover.
    // Mark nav-as-settled too: `BootHealthService` will flip its
    // deadline timer and surface the boot-error UI rather than
    // leaving the loader spinning, which is the right end-of-the-line
    // behaviour when chunk recovery itself has failed.
    clearGuard();
    inject(BootHealthService).markInitialNavSettled();
    return;
  }

  writeGuard(targetUrl);

  const doc = inject(DOCUMENT);
  void primeCacheAndNavigate(doc, targetUrl);
}

async function primeCacheAndNavigate(doc: Document, targetUrl: string): Promise<void> {
  try {
    await fetch(targetUrl, { cache: 'reload' });
  } catch {
    // Network blip during the prime — proceed to navigate anyway. If
    // the network is genuinely down, `assign()` will fail/retry on its
    // own terms; if it's a transient blip, the navigation may still
    // pick up a usable response.
  }
  doc.location.assign(targetUrl);
}
